import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/odstavitev";

export const getWeaningsPage = createAsyncThunk(
  "weaning/getAll",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/odstavitev/page`,
        undefined,
        page,
        filter,
        sort
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createWeaning = createAsyncThunk(
  "weaning/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`${baseUrl}/odstavitev`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateWeaning = createAsyncThunk(
  "weaning/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${baseUrl}/odstavitev/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getWeaningById = createAsyncThunk(
  "weaning/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/odstavitev/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getWeaningPage = createAsyncThunk(
  "weaning/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/odstavitev/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getIndividualWeaningPage = createAsyncThunk(
  "weaning/individual/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/odstavitevMladic/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createIndividualWeaning = createAsyncThunk(
  "individualWeaning/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`${baseUrl}/odstavitevMladic`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateIndividualWeaning = createAsyncThunk(
  "individualWeaning/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `${baseUrl}/odstavitevMladic/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getIndividualWeaningsForWeaningId = createAsyncThunk(
  "individualWeaning/getForWeaningId",
  async (weaningId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/odstavitevMladic/page`,
        null,
        null,
        new ApiFilter().equal("odstavitevId", weaningId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getIndividualWeaningById = createAsyncThunk(
  "individualWeaning/byId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/odstavitevMladic/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  createWeaning,
  updateWeaning,
  createIndividualWeaning,
  updateIndividualWeaning,
  getIndividualWeaningsForWeaningId,
  getWeaningById,
  getWeaningPage,
  getIndividualWeaningPage,
];

const weaningSlice = createSlice({
  name: "weaning",
  initialState: {
    weaning: {},
    individualWeaning: {},
    individualWeaningPage: {},
    isLoading: false,
    error: null,
    page: {},
    individualWeaningByAnimalId: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getWeaningsPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(getWeaningsPage.rejected, (state, action) => {
        state.page = null;
        state.error = action.payload;
      })
      .addCase(createWeaning.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addCase(createIndividualWeaning.fulfilled, (state, action) => {
        state.individualWeaning = action.payload;
      })
      .addCase(getWeaningById.fulfilled, (state, action) => {
        state.weaning = action.payload;
      })
      .addCase(getWeaningPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(getIndividualWeaningPage.fulfilled, (state, action) => {
        state.individualWeaningPage = action.payload;
        action.payload.content?.map((individualWeaning) => {
          state.individualWeaningByAnimalId[individualWeaning.mladicId] =
            individualWeaning;
        });
      })
      .addMatcher(
        isAnyOf(...thunks.map((t) => t.rejected)),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )
      .addMatcher(isAnyOf(...thunks.map((t) => t.pending)), (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isAnyOf(...thunks.map((t) => t.fulfilled)), (state, _) => {
        state.isLoading = false;
      });
  },
});

export default weaningSlice.reducer;
